/**
 * AuditorTabsContext — multi-tab workspace for /auditor (Phase 10 Part B).
 *
 * Each tab owns its own prompt / mode / result / loading state so users can
 * run several audits in parallel and cycle between them. Persistence: the
 * tab list is snapshotted to sessionStorage on every mutation and re-hydrated
 * on mount. sessionStorage clears when the browser session ends — this is
 * deliberate (localStorage would accumulate stale tabs forever).
 *
 * When a tab in the background completes an audit (i.e. the active tab is
 * different from where the audit was fired), consumers can call the exposed
 * `notifyCompletion(tabId)` to raise the "just completed" flag; the bar
 * pulses that tab until the user activates it.
 *
 * Public shape:
 *   { tabs, activeTabId, activeTab, MAX_TABS,
 *     createTab, closeTab, activateTab, renameTab,
 *     updateTab(id, patch), updateActiveTab(patch),
 *     markCompleted(id), clearCompleted(id) }
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "nxa_auditor_tabs";
export const MAX_TABS = 5;

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `tab_${Math.random().toString(36).slice(2)}${Date.now()}`;
}

function newTab(index = 1) {
  return {
    id: makeId(),
    title: `Audit Tab ${index}`,
    prompt: "",
    mode: "quick",
    result: null,
    loading: false,
    error: null,
    justCompleted: false,
    createdAt: new Date().toISOString(),
  };
}

function loadFromStorage() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tabs) || parsed.tabs.length === 0) return null;
    // Sanity — reset transient flags that shouldn't survive refresh
    const tabs = parsed.tabs.map((t) => ({
      ...t,
      loading: false, // never re-hydrate a spinner
      justCompleted: false,
    }));
    const activeTabId =
      tabs.find((t) => t.id === parsed.activeTabId) ? parsed.activeTabId : tabs[0].id;
    return { tabs, activeTabId };
  } catch {
    return null;
  }
}

function saveToStorage(tabs, activeTabId) {
  if (typeof window === "undefined") return;
  try {
    // Snapshot: loading=false so hydration never shows zombie spinners.
    const snapshot = {
      tabs: tabs.map((t) => ({ ...t, loading: false, justCompleted: false })),
      activeTabId,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore quota errors */
  }
}

const AuditorTabsContext = createContext(null);

export function AuditorTabsProvider({ children }) {
  const [initial] = useState(() => {
    const restored = loadFromStorage();
    if (restored) return restored;
    const first = newTab(1);
    return { tabs: [first], activeTabId: first.id };
  });

  const [tabs, setTabs] = useState(initial.tabs);
  const [activeTabId, setActiveTabId] = useState(initial.activeTabId);

  // Persist on any mutation
  useEffect(() => {
    saveToStorage(tabs, activeTabId);
  }, [tabs, activeTabId]);

  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeTabId) || tabs[0] || null,
    [tabs, activeTabId],
  );

  const nextIndex = useCallback(() => {
    const nums = tabs
      .map((t) => {
        const m = /^Audit Tab (\d+)$/.exec(t.title || "");
        return m ? Number(m[1]) : 0;
      })
      .filter((n) => Number.isFinite(n));
    return (nums.length ? Math.max(...nums) : 0) + 1;
  }, [tabs]);

  const createTab = useCallback(() => {
    if (tabs.length >= MAX_TABS) return null;
    const t = newTab(nextIndex());
    setTabs((cur) => [...cur, t]);
    setActiveTabId(t.id);
    return t.id;
  }, [tabs.length, nextIndex]);

  const closeTab = useCallback(
    (id) => {
      setTabs((cur) => {
        const idx = cur.findIndex((t) => t.id === id);
        if (idx === -1) return cur;
        let next = cur.filter((t) => t.id !== id);
        if (next.length === 0) {
          // Never leave the workspace with 0 tabs
          next = [newTab(1)];
        }
        // If we closed the active one, pick a neighbour
        if (id === activeTabId) {
          const nextActive = next[Math.min(idx, next.length - 1)];
          setActiveTabId(nextActive.id);
        }
        return next;
      });
    },
    [activeTabId],
  );

  const activateTab = useCallback((id) => {
    setActiveTabId(id);
    // Activating a background tab clears its just-completed pulse
    setTabs((cur) => cur.map((t) => (t.id === id ? { ...t, justCompleted: false } : t)));
  }, []);

  const renameTab = useCallback((id, title) => {
    const trimmed = (title || "").trim().slice(0, 40) || `Audit Tab`;
    setTabs((cur) => cur.map((t) => (t.id === id ? { ...t, title: trimmed } : t)));
  }, []);

  const updateTab = useCallback((id, patch) => {
    setTabs((cur) => cur.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const updateActiveTab = useCallback(
    (patch) => {
      setTabs((cur) => cur.map((t) => (t.id === activeTabId ? { ...t, ...patch } : t)));
    },
    [activeTabId],
  );

  const markCompleted = useCallback((id) => {
    setTabs((cur) => cur.map((t) => (t.id === id ? { ...t, justCompleted: true } : t)));
  }, []);

  const clearCompleted = useCallback((id) => {
    setTabs((cur) => cur.map((t) => (t.id === id ? { ...t, justCompleted: false } : t)));
  }, []);

  const value = useMemo(
    () => ({
      tabs,
      activeTabId,
      activeTab,
      MAX_TABS,
      createTab,
      closeTab,
      activateTab,
      renameTab,
      updateTab,
      updateActiveTab,
      markCompleted,
      clearCompleted,
    }),
    [tabs, activeTabId, activeTab, createTab, closeTab, activateTab, renameTab, updateTab, updateActiveTab, markCompleted, clearCompleted],
  );

  return <AuditorTabsContext.Provider value={value}>{children}</AuditorTabsContext.Provider>;
}

export function useAuditorTabs() {
  const ctx = useContext(AuditorTabsContext);
  if (!ctx) throw new Error("useAuditorTabs must be used within AuditorTabsProvider");
  return ctx;
}

/** Convenience — returns the currently-active tab + a bound updater. */
export function useActiveTab() {
  const { activeTab, updateActiveTab } = useAuditorTabs();
  return { tab: activeTab, update: updateActiveTab };
}

export default AuditorTabsContext;
